import { Result } from "antd";
import { getLocalizedPath } from "helpers/getLocalizedPath";
import { t } from "i18next";
import { Suspense, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import { Spinner } from "../components/common";
import { getDefaultLanguageKey } from "../helpers/getDefaultLanguage";
import DeveloperShell from "../features/developer/components/DeveloperShell";
import AppLayout from "../layout";
import { setCurrentApp } from "../store/appSlice";
import getPrivilege from "../utils/privileges/privileges";
import { useAppMode } from "./AppModeContext";
import { getAppFromPath, getCurrentApp } from "./appRegistry";

const StaffDirectRouter = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const privileges = useSelector((state) => state.app.privileges);
  const [fmsRoutes, setFmsRoutes] = useState([]);
  const [rmsRoutes, setRmsRoutes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const user = useSelector((state) => state.app?.user?.info);

  useEffect(() => {
    const loadAllRoutes = async () => {
      try {
        const fmsModule = await import("../apps/fms/routes");
        const fmsAppRoutes = fmsModule.default || fmsModule.APP_ROUTES;
        const rmsModule = await import("../apps/rms/routes");
        const rmsAppRoutes = rmsModule.default || rmsModule.APP_ROUTES;
        setFmsRoutes(fmsAppRoutes);
        setRmsRoutes(rmsAppRoutes);
        const currentApp = getAppFromPath(location.pathname) || getCurrentApp();
        if (currentApp) {
          dispatch(setCurrentApp(currentApp.key));
        }
      } catch (error) {
        console.error("Failed to load routes:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAllRoutes();
  }, [location.pathname, dispatch]);

  const getAvailableRoutes = (routes) => {
    if (!routes || !privileges) return [];
    return routes.filter(
      (route) =>
        getPrivilege(route.privilege, privileges) || route.privilege === true,
    );
  };

  const availableFmsRoutes = getAvailableRoutes(fmsRoutes);
  const availableRmsRoutes = getAvailableRoutes(rmsRoutes);

  // Pick the first available page for an app to use as its landing page.
  // Skip routes with path params (e.g. detail/edit pages) since they can't be
  // navigated to without an id. Returns null when the app has no available page.
  const getLandingPath = (routes, appKey) => {
    const landingRoute = routes.find((route) => !route.path.includes(":"));
    return landingRoute ? `/${appKey}${landingRoute.path}` : null;
  };

  const fmsLandingPath = getLandingPath(availableFmsRoutes, "fms");
  const rmsLandingPath = getLandingPath(availableRmsRoutes, "rms");

  // Prefer RMS when the user has it, otherwise fall back to whatever is available.
  const userData = user?.user ?? user;
  const hasRms = (userData?.apps || []).some((app) => app.slug === "rms");
  const homeLandingPath = hasRms
    ? rmsLandingPath || fmsLandingPath
    : fmsLandingPath || rmsLandingPath;

  if (isLoading) {
    return <Spinner size="large" fullPageSpinner />;
  }

  if (!privileges) {
    return <Spinner size="large" fullPageSpinner />;
  }

  const defaultLang = getDefaultLanguageKey() || "en";

  const LanguageRedirect = ({ to }) => {
    const { lang } = useParams();
    const currentLang = lang || defaultLang;
    return <Navigate to={`/${currentLang}${to}`} replace />;
  };

  const renderRoute = ({ Component, path, name }, appKey) => (
    <Route
      key={`${appKey}-${name}`}
      path={`/:lang/${appKey}${path}`}
      element={
        <AppLayout>
          <Suspense fallback={<Spinner size="large" fullPageSpinner />}>
            <Component />
          </Suspense>
        </AppLayout>
      }
    />
  );

  const noAccessElement = (
    <div className="d-grid" style={{ height: "100vh", placeContent: "center" }}>
      <Result
        status="403"
        title="403"
        subTitle={t("Sorry, you don't have access to any page.")}
      />
    </div>
  );

  return (
    <Routes>
      <Route
        path="/"
        element={
          homeLandingPath ? (
            <Navigate to={getLocalizedPath(homeLandingPath)} replace />
          ) : (
            noAccessElement
          )
        }
      />

      <Route
        path="/:lang"
        element={
          homeLandingPath ? (
            <LanguageRedirect to={homeLandingPath} />
          ) : (
            noAccessElement
          )
        }
      />
      {availableFmsRoutes.map((route) => renderRoute(route, "fms"))}
      {availableRmsRoutes.map((route) => renderRoute(route, "rms"))}

      <Route
        path="/:lang/fms"
        element={
          fmsLandingPath ? (
            <LanguageRedirect to={fmsLandingPath} />
          ) : (
            noAccessElement
          )
        }
      />
      <Route
        path="/:lang/rms"
        element={
          rmsLandingPath ? (
            <LanguageRedirect to={rmsLandingPath} />
          ) : (
            noAccessElement
          )
        }
      />

      <Route
        path="*"
        element={
          <div
            className="d-grid"
            style={{ height: "100vh", placeContent: "center" }}
          >
            <Result
              status="404"
              title="404"
              subTitle={t("Sorry, the page you visited does not exist.")}
              extra={<Link to="/">{t("Back to Home")}</Link>}
            />
          </div>
        }
      />
    </Routes>
  );
};

const DeveloperDirectRouter = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const privileges = useSelector((state) => state.app.privileges);
  const [devRoutes, setDevRoutes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const mod = await import("../apps/developer/routes");
        setDevRoutes(mod.APP_ROUTES || []);
        const currentApp = getAppFromPath(location.pathname) || getCurrentApp();
        if (currentApp) {
          dispatch(setCurrentApp(currentApp.key));
        }
      } catch (e) {
        console.error("Failed to load developer routes:", e);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [location.pathname, dispatch]);

  const getAvailableRoutes = (routes) => {
    if (!routes || !privileges) return [];
    return routes.filter(
      (route) =>
        getPrivilege(route.privilege, privileges) || route.privilege === true,
    );
  };

  const available = getAvailableRoutes(devRoutes);

  if (isLoading) {
    return <Spinner size="large" fullPageSpinner />;
  }

  if (!privileges) {
    return <Spinner size="large" fullPageSpinner />;
  }

  const defaultLang = getDefaultLanguageKey() || "en";

  const LanguageRedirect = ({ to }) => {
    const { lang } = useParams();
    const currentLang = lang || defaultLang;
    return <Navigate to={`/${currentLang}${to}`} replace />;
  };

  const renderRoute = ({ Component, path, name }) => (
    <Route
      key={name}
      path={`/:lang/developer${path}`}
      element={
        <DeveloperShell>
          <Suspense fallback={<Spinner size="large" fullPageSpinner />}>
            <Component />
          </Suspense>
        </DeveloperShell>
      }
    />
  );

  return (
    <Routes>
      <Route
        path="/"
        element={
          <Navigate to={getLocalizedPath("/developer/inventory")} replace />
        }
      />
      <Route
        path="/:lang"
        element={<LanguageRedirect to="/developer/inventory" />}
      />
      {available.map((route) => renderRoute(route))}
      <Route
        path="/:lang/developer"
        element={<LanguageRedirect to="/developer/inventory" />}
      />
      <Route
        path="*"
        element={
          <div
            className="d-grid"
            style={{ height: "100vh", placeContent: "center" }}
          >
            <Result
              status="404"
              title="404"
              subTitle={t("Sorry, the page you visited does not exist.")}
              extra={<Link to="/">{t("Back to Home")}</Link>}
            />
          </div>
        }
      />
    </Routes>
  );
};

const DirectRouter = () => {
  const { portalMode } = useAppMode();
  if (portalMode === "developer") {
    return <DeveloperDirectRouter />;
  }
  return <StaffDirectRouter />;
};

export default DirectRouter;
